// src/api/bookings.js
import { gatewayFetch } from './gateway.js';
import { getCurrentUser } from './auth.js';

// Global In-Memory Cache
let bookingsCache = null;

export async function createBooking(bookingData) {
  const user = getCurrentUser();

  if (!bookingData.address || !bookingData.scheduledDateTime) {
    throw new Error('Address and Scheduled Date/Time are required.');
  }

  const payload = {
    requested_date: bookingData.scheduledDateTime,
    job_address: bookingData.address.trim(),
    job_description: bookingData.jobDescription?.trim() || 'General handyman service request',
    phone: bookingData.phone?.trim() || user?.companyNumber || '',
    items: (bookingData.items || []).map(i => ({
      service_name: i.service_name,
      quantity: Number(i.quantity) || 1.0
    }))
  };

  // Invalidate cache on new booking
  bookingsCache = null;

  return await gatewayFetch('/api/v1/public/bookings/request', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

/**
 * Fast-fetching client bookings with Stale-While-Revalidate caching
 */
export async function getClientBookingsData(forceRefresh = false) {
  if (bookingsCache && !forceRefresh) {
    // Return cached memory state instantly while refreshing in background
    refreshBookingsBackground();
    return bookingsCache;
  }

  return await fetchAndCacheBookings();
}

async function fetchAndCacheBookings() {
  try {
    const [bookingsRes, availabilityRes] = await Promise.all([
      gatewayFetch('/api/v1/public/bookings/').catch(() => ({})),
      gatewayFetch('/api/v1/public/availability/').catch(() => ({}))
    ]);

    const rawJobs = bookingsRes.jobs || bookingsRes.bookings || (Array.isArray(bookingsRes) ? bookingsRes : []);
    const jobs = Array.isArray(rawJobs) ? rawJobs : [];

    const availBlocked = availabilityRes.blocked_dates || availabilityRes.unavailableDates || [];
    const bookingsBlocked = bookingsRes.blocked_dates || [];
    const combinedBlocked = Array.from(new Set([...availBlocked, ...bookingsBlocked]));

    bookingsCache = { jobs, blockedDates: combinedBlocked };
    return bookingsCache;
  } catch (err) {
    console.error('Error fetching client bookings & availability:', err);
    return bookingsCache || { jobs: [], blockedDates: [] };
  }
}

async function refreshBookingsBackground() {
  fetchAndCacheBookings().catch(() => {});
}

export async function scopeChangeBooking(jobId, scopePayload) {
  bookingsCache = null; // Invalidate cache on scope mutations
  return await gatewayFetch(`/api/v1/public/bookings/${jobId}/scope-change`, {
    method: 'POST',
    body: JSON.stringify({
      items_to_add: (scopePayload.items_to_add || []).map(i => ({
        service_name: i.service_name,
        quantity: Number(i.quantity) || 1.0
      })),
      items_to_remove: scopePayload.items_to_remove || [],
      cancel_booking: Boolean(scopePayload.cancel_booking),
      target_date: scopePayload.target_date || null
    })
  });
}

export async function rescheduleBooking(jobId, targetDate) {
  return await scopeChangeBooking(jobId, { target_date: targetDate });
}

export async function sendJobNote(jobId, noteText) {
  return await gatewayFetch(`/api/v1/public/bookings/${jobId}/notes`, {
    method: 'POST',
    body: JSON.stringify({ note_text: noteText })
  });
}

export async function triggerBookingDeposit(bookingId, customerEmail) {
  return await gatewayFetch(`/api/v1/public/bookings/${bookingId}/deposit`, {
    method: 'POST',
    body: JSON.stringify({
      booking_id: Number(bookingId),
      customer_email: String(customerEmail).trim()
    })
  });
}